import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart3,
  Users,
  Clock,
  AlertOctagon,
  CheckCircle2,
  UserCog,
} from "lucide-react";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

export default function ReportsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["reports-overview"],
    queryFn: api.reportsOverview,
  });

  return (
    <main
      className="max-w-[1200px] mx-auto px-4 md:px-8 py-6 flex flex-col gap-6"
      data-testid="reports-page"
    >
      <header>
        <h1 className="text-lg font-semibold text-[#0F172A] flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-[#64748B]" /> Firm Reports
        </h1>
        <p className="text-xs text-[#64748B]">
          Firm-wide compliance KPIs across every client and staff.
        </p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Kpi
          icon={Users}
          label="Active Clients"
          value={data?.clients?.active}
          total={data?.clients?.total}
          loading={isLoading}
          accent={{ bg: "#F1F5F9", fg: "#0F172A" }}
        />
        <Kpi
          icon={Clock}
          label="Pending Tasks"
          value={data?.tasks?.pending}
          loading={isLoading}
          accent={{ bg: "#FEF3C7", fg: "#92400E" }}
        />
        <Kpi
          icon={AlertOctagon}
          label="Overdue"
          value={data?.tasks?.overdue}
          loading={isLoading}
          accent={{ bg: "#FEE2E2", fg: "#991B1B" }}
        />
        <Kpi
          icon={CheckCircle2}
          label="Completed"
          value={data?.tasks?.completed}
          loading={isLoading}
          accent={{ bg: "#DCFCE7", fg: "#166534" }}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Panel title="By Category" testId="reports-category">
          {isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (data?.byCategory || []).length === 0 ? (
            <Empty label="No tasks yet" />
          ) : (
            <table className="ab-table w-full text-left">
              <thead>
                <tr>
                  <th>Category</th>
                  <th className="text-right">Pending</th>
                  <th className="text-right">Completed</th>
                  <th className="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.byCategory.map((r) => (
                  <tr key={r.category}>
                    <td className="font-medium text-[#0F172A]">{r.category}</td>
                    <td className="text-right tabular-nums">{r.pending}</td>
                    <td className="text-right tabular-nums text-[#166534]">{r.completed}</td>
                    <td className="text-right tabular-nums font-semibold">{r.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title="Top Overdue Clients" testId="reports-top-overdue">
          {isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (data?.topOverdue || []).length === 0 ? (
            <Empty label="No overdue tasks — great job!" />
          ) : (
            <ul className="divide-y divide-[#E2E8F0]">
              {data.topOverdue.map((c) => (
                <li
                  key={c.clientId}
                  className="px-4 py-2.5 flex items-center justify-between"
                >
                  <span className="text-sm text-[#0F172A] truncate pr-2">{c.name}</span>
                  <span className="text-xs font-semibold bg-[#FEE2E2] text-[#991B1B] rounded px-1.5 py-0.5 tabular-nums">
                    {c.overdue} overdue
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <Panel
        title={
          <span className="inline-flex items-center gap-1.5">
            <UserCog className="h-3.5 w-3.5 text-[#64748B]" /> Workload by Staff
          </span>
        }
        testId="reports-by-staff"
      >
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : (data?.byStaff || []).length === 0 ? (
          <Empty label="No staff accounts yet" />
        ) : (
          <table className="ab-table w-full text-left">
            <thead>
              <tr>
                <th>Staff</th>
                <th className="text-right">Clients</th>
                <th className="text-right">Pending</th>
                <th className="text-right">Overdue</th>
                <th className="text-right">Completed</th>
              </tr>
            </thead>
            <tbody>
              {data.byStaff.map((s) => (
                <tr key={s.staffId} data-testid={`reports-staff-row-${s.staffId}`}>
                  <td className="font-medium text-[#0F172A]">{s.name}</td>
                  <td className="text-right tabular-nums">{s.clients}</td>
                  <td className="text-right tabular-nums">{s.pending}</td>
                  <td className="text-right tabular-nums">
                    {s.overdue > 0 ? (
                      <span className="text-[#DC2626] font-semibold">{s.overdue}</span>
                    ) : (
                      <span className="text-[#94A3B8]">0</span>
                    )}
                  </td>
                  <td className="text-right tabular-nums text-[#166534]">{s.completed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </main>
  );
}

function Kpi({ icon: Icon, label, value, total, loading, accent }) {
  return (
    <div className="ab-card ab-shadow-sm px-4 py-3.5 flex items-center gap-3">
      <div
        className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: accent.bg, color: accent.fg }}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-[#64748B] font-medium">
          {label}
        </div>
        <div className="text-xl font-semibold text-[#0F172A] tabular-nums mt-0.5">
          {loading ? (
            <Skeleton className="h-6 w-10" />
          ) : (
            <>
              {value ?? 0}
              {total !== undefined && (
                <span className="text-xs text-[#94A3B8] font-normal ml-1">
                  / {total}
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Panel({ title, testId, children }) {
  return (
    <section
      className="ab-card ab-shadow-sm overflow-hidden"
      data-testid={testId}
    >
      <div className="px-4 py-2.5 border-b border-[#E2E8F0] bg-[#F8FAFC] text-xs font-semibold uppercase tracking-wider text-[#334155]">
        {title}
      </div>
      {children}
    </section>
  );
}

function Empty({ label }) {
  return <div className="p-6 text-center text-sm text-[#64748B]">{label}</div>;
}
